/**
 * Superadmin: blogartikel bewerken of nieuw aanmaken.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { SeoMeta } from '../../components/SeoMeta';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';
import type { BlogPostAdmin } from '../../types/blog';

function toLocalInput(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
}

function fromLocalInput(s: string): string {
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
}

const emptyForm: Omit<BlogPostAdmin, 'slug'> = {
  title: '',
  excerpt: '',
  body_html: '',
  meta_title: '',
  meta_description: '',
  keywords: '',
  category: 'Bouw & renovatie',
  author: 'CalqTool redactie',
  reading_minutes: 6,
  published: 1,
  published_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

export function AdminBlogEditor() {
  const { slug: rawSlug } = useParams<{ slug: string }>();
  const slugParam = rawSlug ? decodeURIComponent(rawSlug) : '';
  const isCreate = slugParam === 'nieuw';
  const { user } = useAuth();
  const isSuper = user?.role === 'superadmin';
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [slugNew, setSlugNew] = useState('');
  const [f, setF] = useState(emptyForm);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-blog-post', slugParam],
    queryFn: () => api.getAdminBlogPost(slugParam),
    enabled: !!user?.authenticated && isSuper && !isCreate,
  });

  useEffect(() => {
    if (isCreate) {
      setSlugNew('');
      setF({ ...emptyForm, published_at: new Date().toISOString(), updated_at: new Date().toISOString() });
      return;
    }
    const p = data?.post;
    if (!p) return;
    setF({
      title: p.title,
      excerpt: p.excerpt,
      body_html: p.body_html,
      meta_title: p.meta_title,
      meta_description: p.meta_description,
      keywords: p.keywords,
      category: p.category,
      author: p.author,
      reading_minutes: p.reading_minutes,
      published: p.published,
      published_at: p.published_at,
      updated_at: p.updated_at,
    });
  }, [isCreate, data?.post]);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (isCreate) {
        const s = slugNew.trim().toLowerCase();
        if (!s) throw new Error('Vul een slug in.');
        await api.postAdminBlogPost({
          slug: s,
          title: f.title.trim(),
          excerpt: f.excerpt.trim(),
          body_html: f.body_html,
          meta_title: f.meta_title.trim() || undefined,
          meta_description: f.meta_description.trim() || undefined,
          keywords: f.keywords.trim() || undefined,
          category: f.category.trim() || undefined,
          author: f.author.trim() || undefined,
          reading_minutes: f.reading_minutes,
          published: f.published,
          published_at: fromLocalInput(toLocalInput(f.published_at)) || undefined,
        });
        return s;
      }
      await api.putAdminBlogPost(slugParam, {
        title: f.title.trim(),
        excerpt: f.excerpt.trim(),
        body_html: f.body_html,
        meta_title: f.meta_title.trim(),
        meta_description: f.meta_description.trim(),
        keywords: f.keywords.trim(),
        category: f.category.trim(),
        author: f.author.trim(),
        reading_minutes: f.reading_minutes,
        published: f.published,
        published_at: fromLocalInput(toLocalInput(f.published_at)),
      });
      return slugParam;
    },
    onSuccess: async (savedSlug) => {
      await qc.invalidateQueries({ queryKey: ['admin-blog-posts'] });
      await qc.invalidateQueries({ queryKey: ['blog-posts'] });
      await qc.invalidateQueries({ queryKey: ['blog-post'] });
      await qc.invalidateQueries({ queryKey: ['admin-blog-post'] });
      if (isCreate) {
        navigate(`/admin/cms/blog/${encodeURIComponent(savedSlug)}`, { replace: true });
      }
    },
  });

  if (!isSuper) {
    return <Navigate to="/admin" replace />;
  }

  const errMsg = error instanceof Error ? error.message : '';
  const saveErr = saveMut.error instanceof Error ? saveMut.error.message : '';

  const field = (label: string, child: ReactNode) => (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-text-muted">{label}</span>
      {child}
    </label>
  );

  return (
    <div>
      <SeoMeta
        path={isCreate ? '/admin/cms/blog/nieuw' : `/admin/cms/blog/${slugParam}`}
        noindex
        title={isCreate ? 'Nieuw blogartikel' : `Blog: ${slugParam}`}
      />
      <Breadcrumbs
        items={[
          { label: 'Platformbeheer', href: '/admin' },
          { label: 'CMS', href: '/admin/cms' },
          { label: 'Blog', href: '/admin/cms/blog' },
          { label: isCreate ? 'Nieuw' : slugParam },
        ]}
      />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-text">{isCreate ? 'Nieuw artikel' : 'Artikel bewerken'}</h1>
        <Link to="/admin/cms/blog" className="text-sm font-medium text-primary hover:underline">
          ← Overzicht
        </Link>
      </div>

      {!isCreate && isLoading ? (
        <p className="text-text-muted">Laden…</p>
      ) : !isCreate && errMsg ? (
        <p className="text-red-600 dark:text-red-400">{errMsg}</p>
      ) : (
        <form
          className="max-w-4xl space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            saveMut.mutate();
          }}
        >
          {isCreate
            ? field(
                'Slug (URL)',
                <input
                  required
                  value={slugNew}
                  onChange={(e) => setSlugNew(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  className="w-full rounded-lg border border-border bg-surface-card px-3 py-2 font-mono text-sm"
                  placeholder="bijv. mijn-onderwerp-2026"
                />
              )
            : field(
                'Slug',
                <input readOnly value={slugParam} className="w-full rounded-lg border border-border bg-surface px-3 py-2 font-mono text-sm text-text-muted" />
              )}

          {field(
            'Titel',
            <input
              required
              value={f.title}
              onChange={(e) => setF((x) => ({ ...x, title: e.target.value }))}
              className="w-full rounded-lg border border-border bg-surface-card px-3 py-2 text-sm"
            />
          )}

          {field(
            'Samenvatting (excerpt)',
            <textarea
              required
              rows={3}
              value={f.excerpt}
              onChange={(e) => setF((x) => ({ ...x, excerpt: e.target.value }))}
              className="w-full rounded-lg border border-border bg-surface-card px-3 py-2 text-sm"
            />
          )}

          {field(
            'HTML-inhoud',
            <textarea
              required
              rows={18}
              value={f.body_html}
              onChange={(e) => setF((x) => ({ ...x, body_html: e.target.value }))}
              className="w-full rounded-lg border border-border bg-surface-card px-3 py-2 font-mono text-xs leading-relaxed"
              spellCheck={false}
            />
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            {field(
              'Meta title (SEO)',
              <input
                value={f.meta_title}
                onChange={(e) => setF((x) => ({ ...x, meta_title: e.target.value }))}
                className="w-full rounded-lg border border-border bg-surface-card px-3 py-2 text-sm"
              />
            )}
            {field(
              'Categorie',
              <input
                value={f.category}
                onChange={(e) => setF((x) => ({ ...x, category: e.target.value }))}
                className="w-full rounded-lg border border-border bg-surface-card px-3 py-2 text-sm"
              />
            )}
          </div>

          {field(
            'Meta description (SEO)',
            <textarea
              rows={2}
              value={f.meta_description}
              onChange={(e) => setF((x) => ({ ...x, meta_description: e.target.value }))}
              className="w-full rounded-lg border border-border bg-surface-card px-3 py-2 text-sm"
            />
          )}

          {field(
            'Keywords (komma’s)',
            <input
              value={f.keywords}
              onChange={(e) => setF((x) => ({ ...x, keywords: e.target.value }))}
              className="w-full rounded-lg border border-border bg-surface-card px-3 py-2 text-sm"
            />
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            {field(
              'Auteur',
              <input
                value={f.author}
                onChange={(e) => setF((x) => ({ ...x, author: e.target.value }))}
                className="w-full rounded-lg border border-border bg-surface-card px-3 py-2 text-sm"
              />
            )}
            {field(
              'Leestijd (min)',
              <input
                type="number"
                min={1}
                max={120}
                value={f.reading_minutes}
                onChange={(e) => setF((x) => ({ ...x, reading_minutes: Math.max(1, Number(e.target.value) || 1) }))}
                className="w-full rounded-lg border border-border bg-surface-card px-3 py-2 text-sm"
              />
            )}
            {field(
              'Publicatiedatum',
              <input
                type="datetime-local"
                value={toLocalInput(f.published_at)}
                onChange={(e) => setF((x) => ({ ...x, published_at: fromLocalInput(e.target.value) }))}
                className="w-full rounded-lg border border-border bg-surface-card px-3 py-2 text-sm"
              />
            )}
          </div>

          <label className="flex items-center gap-2 text-sm text-text">
            <input
              type="checkbox"
              checked={f.published === 1}
              onChange={(e) => setF((x) => ({ ...x, published: e.target.checked ? 1 : 0 }))}
            />
            Gepubliceerd (zichtbaar op /blog)
          </label>

          {saveErr ? <p className="text-sm text-red-600 dark:text-red-400">{saveErr}</p> : null}

          <div className="flex flex-wrap gap-2 pt-2">
            <button
              type="submit"
              disabled={saveMut.isPending}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-alt disabled:opacity-60"
            >
              {saveMut.isPending ? 'Opslaan…' : 'Opslaan'}
            </button>
            <Link to="/blog" target="_blank" rel="noopener noreferrer" className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-surface">
              Publieke blog (nieuw tabblad)
            </Link>
          </div>
        </form>
      )}
    </div>
  );
}
